import { BIND_FORMATS, type BindV3, type FieldSpec } from '@docgen/schema';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { ImageUploadField } from '@bcecloud/portal-ui';
import { useImageUpload } from './image-upload';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { NativeSelect } from '../ui/select';
import { Textarea } from '../ui/textarea';

/**
 * Literal editing controls generated from a block's FieldSpec metadata.
 * Binding awareness lives one level up (Inspector's FieldRow): these
 * controls edit whatever value they're handed — a literal prop or the data
 * a bind points at — via `onChange`.
 */

export interface PropControlProps {
  field: FieldSpec;
  value: unknown;
  onChange: (v: unknown) => void;
  /** rendered on the label row's right side (link chip / actions) */
  action?: ReactNode;
}

export function BindEditor({ value, onChange, dataPaths }: {
  value: BindV3;
  onChange: (v: unknown) => void;
  dataPaths: string[];
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Input
        list="docgen-data-paths"
        value={value.$bind}
        placeholder="data path, e.g. client.name"
        className="font-mono text-xs"
        onChange={(e) => onChange({ ...value, $bind: e.target.value })}
      />
      <datalist id="docgen-data-paths">
        {dataPaths.map((p) => (
          <option key={p} value={p} />
        ))}
      </datalist>
      <NativeSelect
        value={value.format ?? ''}
        className="w-24"
        aria-label="Format"
        onChange={(e) => {
          const format = e.target.value;
          onChange(format ? { ...value, format } : { $bind: value.$bind });
        }}
      >
        <option value="">raw</option>
        {BIND_FORMATS.map((f) => (
          <option key={f} value={f}>{f}</option>
        ))}
      </NativeSelect>
    </div>
  );
}

function LiteralControl({ field, value, onChange }: Omit<PropControlProps, 'action'>) {
  const text = value === null || value === undefined ? '' : String(value);
  if (field.kind === 'text' && field.multiline) {
    return (
      <Textarea
        value={text}
        rows={Math.min(6, Math.max(2, Math.ceil(text.length / 55)))}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  switch (field.kind) {
    case 'richtext':
      return (
        <Textarea
          value={text}
          rows={4}
          className="font-mono text-xs"
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'number':
      return (
        <Input
          type="number"
          value={typeof value === 'number' ? value : text}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
        />
      );
    case 'select':
      return (
        <NativeSelect value={text} onChange={(e) => onChange(e.target.value)}>
          {!field.options.includes(text) ? <option value={text}>{text || '—'}</option> : null}
          {field.options.map((o) => (
            <option key={o} value={o}>{o || '—'}</option>
          ))}
        </NativeSelect>
      );
    default:
      return <Input value={text} onChange={(e) => onChange(e.target.value)} />;
  }
}

/** Accordion list editor: rows collapse to their leading text; expand to edit. */
function ListControl({ field, value, onChange }: Omit<PropControlProps, 'action'> & {
  field: Extract<FieldSpec, { kind: 'list' }>;
}) {
  const rows = Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
  const [open, setOpen] = useState<Set<number>>(() => new Set());
  const setRows = (next: Record<string, unknown>[]) => onChange(next);
  const toggle = (i: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  const summaryField = field.item[0]?.name;
  const summarize = (row: Record<string, unknown>, i: number): string => {
    const v = summaryField !== undefined ? row[summaryField] : undefined;
    const s = v === null || v === undefined ? '' : String(v).trim();
    return s !== '' ? s : `Item ${i + 1}`;
  };
  return (
    <div className="space-y-1">
      {rows.map((row, i) => {
        const expanded = open.has(i);
        return (
          <div key={i} className="overflow-hidden rounded-md border bg-background/60">
            <div
              className="flex w-full cursor-pointer items-center gap-1 px-1.5 py-1 text-left hover:bg-accent/60"
              onClick={() => toggle(i)}
            >
              {expanded ? (
                <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
              )}
              <span className="min-w-0 flex-1 truncate text-xs">{summarize(row, i)}</span>
              <span className="flex shrink-0 gap-0" onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-5 w-5" aria-label="Move up" disabled={i === 0}
                  onClick={() => {
                    const next = [...rows];
                    [next[i - 1], next[i]] = [next[i]!, next[i - 1]!];
                    setRows(next);
                  }}>
                  <ArrowUp className="!size-3" />
                </Button>
                <Button variant="ghost" size="icon" className="h-5 w-5" aria-label="Move down"
                  disabled={i === rows.length - 1}
                  onClick={() => {
                    const next = [...rows];
                    [next[i], next[i + 1]] = [next[i + 1]!, next[i]!];
                    setRows(next);
                  }}>
                  <ArrowDown className="!size-3" />
                </Button>
                <Button variant="ghost" size="icon" className="h-5 w-5" aria-label="Remove row"
                  onClick={() => {
                    setRows(rows.filter((_, ri) => ri !== i));
                    setOpen((prev) => new Set([...prev].filter((n) => n !== i).map((n) => (n > i ? n - 1 : n))));
                  }}>
                  <Trash2 className="!size-3" />
                </Button>
              </span>
            </div>
            {expanded && (
              <div className="space-y-1.5 border-t p-2">
                {field.item.map((itemField) => (
                  <PropControl
                    key={itemField.name}
                    field={itemField}
                    value={row[itemField.name]}
                    onChange={(v) =>
                      setRows(rows.map((r, ri) => (ri === i ? { ...r, [itemField.name]: v } : r)))
                    }
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
      <Button variant="outline" size="sm"
        onClick={() => {
          setRows([
            ...rows,
            Object.fromEntries(
              field.item.map((f) => [f.name, 'default' in f ? (f.default ?? '') : '']),
            ),
          ]);
          setOpen((prev) => new Set([...prev, rows.length]));
        }}>
        <Plus /> Add item
      </Button>
    </div>
  );
}

export function PropControl({ field, value, onChange, action }: PropControlProps) {
  const upload = useImageUpload();
  const effective = value === undefined && 'default' in field ? field.default : value;
  if (field.kind === 'image' && upload) {
    // Upload first: sales builds pages from photos on their computer, not
    // from images already hosted somewhere. Pasting a URL stays available.
    return (
      <ImageUploadField
        label={field.label}
        value={effective === null || effective === undefined ? '' : String(effective)}
        onChange={onChange}
        upload={upload}
        {...(action === undefined ? {} : { action })}
      />
    );
  }
  return (
    <div className="space-y-1">
      <Label className="flex min-h-4 items-center justify-between gap-2">
        <span className="truncate">{field.label}</span>
        {action}
      </Label>
      {field.kind === 'list' ? (
        <ListControl field={field} value={value} onChange={onChange} />
      ) : (
        <LiteralControl field={field} value={effective} onChange={onChange} />
      )}
    </div>
  );
}

/** Enumerate ARRAY paths of a data tree (candidates for repeat.over). Does
 *  not descend into array items — per-item arrays are reachable only through
 *  a repeat alias, which the caller adds. */
export function collectArrayPaths(data: unknown, prefix = '', depth = 0): string[] {
  if (depth > 4 || data === null || typeof data !== 'object') return [];
  if (Array.isArray(data)) return prefix ? [prefix] : [];
  const out: string[] = [];
  for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
    out.push(...collectArrayPaths(v, prefix ? `${prefix}.${k}` : k, depth + 1));
  }
  return out;
}

/** Enumerate dot-paths of a data tree (arrays contribute `path.0…` samples). */
export function collectDataPaths(data: unknown, prefix = '', depth = 0): string[] {
  if (depth > 4 || data === null || typeof data !== 'object') return prefix ? [prefix] : [];
  const out: string[] = prefix ? [prefix] : [];
  if (Array.isArray(data)) {
    if (data.length > 0) out.push(...collectDataPaths(data[0], `${prefix}.0`, depth + 1));
    return out;
  }
  for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
    out.push(...collectDataPaths(v, prefix ? `${prefix}.${k}` : k, depth + 1));
  }
  return out;
}
